import React from "react";
import { createPortal } from "react-dom";
import { AlertCircle, ArrowRight, CheckCircle, Mail, X, Zap } from "./Icons";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
}) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/60 z-modal flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800">
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50 rounded-t-2xl">
          <h3 className="font-bold text-lg text-slate-800 dark:text-white">
            {title}
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>,
    document.body,
  );
};

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  confirmVariant?: "danger" | "primary";
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  confirmVariant = "primary",
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-6">
        <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
          {message}
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            data-testid="confirm-modal-cancel"
            className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm font-medium transition"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            data-testid="confirm-modal-confirm"
            className={`px-4 py-2 text-white rounded-lg font-medium text-sm shadow-lg transition active:scale-95 ${
              confirmVariant === "danger"
                ? "bg-red-600 hover:bg-red-700 shadow-red-500/20"
                : "bg-indigo-600 hover:bg-indigo-700 shadow-indigo-500/20"
            }`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
};

interface ErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message: string;
}

interface SuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message: string;
}

export const SuccessModal: React.FC<SuccessModalProps> = ({
  isOpen,
  onClose,
  title = "Success",
  message,
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-6 text-center">
        <div className="flex justify-center">
          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-full">
            <CheckCircle
              className="text-green-600 dark:text-green-400"
              size={48}
            />
          </div>
        </div>
        <p
          data-testid="success-modal-message"
          className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed"
        >
          {message}
        </p>
        <div className="flex justify-center">
          <button
            onClick={onClose}
            data-testid="success-modal-dismiss"
            className="px-8 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm shadow-lg shadow-indigo-500/20 transition active:scale-95"
          >
            Great!
          </button>
        </div>
      </div>
    </Modal>
  );
};

interface RoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (role: string) => void;
  title: string;
  currentRole: string;
  roles: { value: string; label: string; hint: string }[];
}

export const RoleModal: React.FC<RoleModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  currentRole,
  roles,
}) => {
  const [selected, setSelected] = React.useState(currentRole);

  React.useEffect(() => {
    if (isOpen) setSelected(currentRole);
  }, [isOpen, currentRole]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-6">
        <div className="space-y-3">
          {roles.map((role) => (
            <button
              key={role.value}
              onClick={() => setSelected(role.value)}
              className={`w-full text-left p-4 rounded-xl border transition-all ${
                selected === role.value
                  ? "border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-indigo-600"
                  : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
              }`}
            >
              <div className="flex justify-between items-center mb-1">
                <span
                  className={`font-bold text-sm ${selected === role.value ? "text-indigo-700 dark:text-indigo-300" : "text-slate-700 dark:text-slate-300"}`}
                >
                  {role.label}
                </span>
                {selected === role.value && (
                  <div className="w-4 h-4 rounded-full bg-indigo-600 flex items-center justify-center">
                    <CheckCircle size={12} className="text-white" />
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                {role.hint}
              </p>
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm font-medium transition"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm(selected);
              onClose();
            }}
            className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium text-sm shadow-lg shadow-indigo-500/20 transition active:scale-95"
          >
            Update Role
          </button>
        </div>
      </div>
    </Modal>
  );
};

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (email: string) => void;
}

export const InviteModal: React.FC<InviteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const [email, setEmail] = React.useState("");
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (isOpen) {
      setEmail("");
      setError("");
    }
  }, [isOpen]);

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const handleConfirm = () => {
    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    onConfirm(email);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Invite Member">
      <div className="space-y-6">
        <div className="flex flex-col items-center text-center space-y-2 mb-2">
          <div className="p-3 bg-indigo-50 dark:bg-indigo-900/30 rounded-full text-indigo-600 dark:text-indigo-400 mb-2">
            <Mail size={32} />
          </div>
          <h4 className="text-lg font-bold text-slate-900 dark:text-white">
            Grow your team
          </h4>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Send an invitation to join your workspace and start collaborating.
          </p>
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">
            Email Address
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError("");
            }}
            data-testid="invite-email-input"
            placeholder="colleague@company.com"
            className={`w-full border ${error ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl p-3.5 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition shadow-sm outline-none text-sm`}
            autoFocus
          />
          {error && (
            <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
              <AlertCircle size={12} /> {error}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-3">
          <button
            onClick={handleConfirm}
            data-testid="invite-submit-button"
            className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-indigo-500/20 transition active:scale-95 hover:bg-indigo-700 flex items-center justify-center gap-2"
          >
            Send Invitation <ArrowRight size={16} />
          </button>
          <button
            onClick={onClose}
            className="w-full py-2.5 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl text-sm font-medium transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export const ErrorModal: React.FC<ErrorModalProps> = ({
  isOpen,
  onClose,
  title = "Error",
  message,
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-6">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-full shrink-0">
            <AlertCircle className="text-red-600 dark:text-red-400" size={24} />
          </div>
          <p
            data-testid="error-modal-message"
            className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed pt-1"
          >
            {message}
          </p>
        </div>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            data-testid="error-modal-dismiss"
            className="px-6 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg font-medium text-sm transition active:scale-95"
          >
            Dismiss
          </button>
        </div>
      </div>
    </Modal>
  );
};

interface LimitReachedModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  onUpgrade: () => void;
}

export const LimitReachedModal: React.FC<LimitReachedModalProps> = ({
  isOpen,
  onClose,
  title = "Limit Reached",
  message,
  onUpgrade,
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-6">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-full shrink-0">
            <Zap className="text-indigo-600 dark:text-indigo-400" size={24} />
          </div>
          <div className="pt-1">
            <p className="text-slate-900 dark:text-white font-bold text-sm mb-1">
              {title}
            </p>
            <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
              {message}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <button
            onClick={() => {
              onUpgrade();
              onClose();
            }}
            className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-indigo-500/20 transition active:scale-95 hover:bg-indigo-700 flex items-center justify-center gap-2"
          >
            Upgrade to Team <ArrowRight size={16} />
          </button>
          <button
            onClick={onClose}
            className="w-full py-2.5 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl text-sm font-medium transition"
          >
            Not now
          </button>
        </div>
      </div>
    </Modal>
  );
};
